import { Link } from "react-router";
import { compactNumber, duration, usd } from "../../lib/format";
import { issueKey } from "../../lib/tasks";
import type { AgentSummary, RunHistoryItem } from "../../types";
import { ColorDot, StatusBadge, ToneBadge } from "../status-badge";
import { TimeAgo } from "../time-ago";
import { TableCell, TableRow } from "../ui/table";

const NONE = <span className="text-muted-foreground/60">—</span>;

export function RunHistoryRow({
  run,
  agent,
  selected,
  onOpen,
}: {
  run: RunHistoryItem;
  agent: AgentSummary | undefined;
  selected: boolean;
  onOpen: () => void;
}) {
  const key = issueKey(run.task);
  const note = run.error ?? run.blockedReason ?? run.summary;
  const cost = run.usage?.costUsd;
  const tokens = run.usage?.totalTokens;
  return (
    <TableRow
      className="group/row cursor-pointer"
      data-state={selected ? "selected" : undefined}
      onClick={onOpen}
    >
      <TableCell className="whitespace-nowrap">
        <span className="flex items-center gap-2">
          {agent && <ColorDot color={agent.station.color} />}
          {agent?.name ?? run.agentId}
          {run.attempt > 1 && <ToneBadge tone="amber">#{run.attempt}</ToneBadge>}
        </span>
      </TableCell>
      <TableCell className="max-w-0 w-full">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="shrink-0 font-mono text-[11px] text-muted-foreground">{key ?? "Manual"}</span>
          <Link
            to={`/tasks/${run.taskId}`}
            className="truncate decoration-border underline-offset-[3px] group-hover/row:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {run.task.title}
          </Link>
        </div>
        {note && (
          <p
            className={
              run.error ? "truncate text-xs text-destructive" : "truncate text-xs text-muted-foreground"
            }
            title={note}
          >
            {note}
          </p>
        )}
      </TableCell>
      <TableCell>
        <StatusBadge status={run.status} />
      </TableCell>
      <TableCell className="hidden md:table-cell">
        {run.verdict ? <ToneBadge tone="violet">{run.verdict}</ToneBadge> : NONE}
      </TableCell>
      <TableCell className="hidden text-xs text-muted-foreground xl:table-cell">{run.provider}</TableCell>
      <TableCell className="hidden text-right text-xs whitespace-nowrap text-muted-foreground tabular-nums sm:table-cell">
        {run.startedAt ? duration(run.startedAt, run.finishedAt) : NONE}
      </TableCell>
      <TableCell
        className="hidden text-right text-xs text-muted-foreground tabular-nums xl:table-cell"
        title={tokens === undefined ? undefined : `${tokens.toLocaleString()} tokens`}
      >
        {tokens === undefined ? NONE : compactNumber(tokens)}
      </TableCell>
      <TableCell className="hidden text-right text-xs tabular-nums lg:table-cell">
        {cost === undefined ? NONE : usd(cost)}
      </TableCell>
      <TableCell className="hidden text-right text-xs whitespace-nowrap text-muted-foreground tabular-nums lg:table-cell">
        <TimeAgo at={run.createdAt} />
      </TableCell>
    </TableRow>
  );
}
